import {
  ClipboardCopyIcon,
  ColorSwatchIcon,
  DeviceTabletIcon,
  ExternalLinkIcon,
  RefreshIcon,
  SparklesIcon,
  SwitchHorizontalIcon,
} from '@heroicons/react/outline';
import { StaticImage } from 'gatsby-plugin-image';
import * as React from 'react';
import * as staticPaths from '../../staticPaths';

const features = [
  {
    id: 'random',
    name: 'Generate a random, manual, or hybrid seating chart.',
    description:
      'Randomize with a press of a button and manually adjust with drag & drop. Re-use saved rooms and student lists.',
    icon: SparklesIcon,
  },
  {
    id: 'groups',
    name: 'Use smart grouping with random seating.',
    description:
      'Assign students to groups to seat them together, apart, or, for example, in the front row. Random seating respects your groupings.',
    icon: SwitchHorizontalIcon,
  },
  {
    id: 'import',
    name: 'Import student names easily.',
    description:
      'Copy and paste student names from any spreadsheet or application into the seating plan generator.',
    icon: ClipboardCopyIcon,
  },
  {
    id: 'wheel',
    name: 'Pick with wheel of names',
    description: (
      <p>
        Use the{' '}
        <a
          href={staticPaths.wheelOfNames}
          target="_blank"
          rel="noreferrer"
          className="text-indigo-300 hover:underline"
        >
          wheel of names <ExternalLinkIcon className="h-4 w-4 inline-block" />
        </a>{' '}
        to pick a random student from your roster. Add excitement, make unbiased
        decisions, or play games.
      </p>
    ),
    icon: RefreshIcon,
  },
  {
    id: 'visual',
    name: 'Choose from multiple visual options.',
    description:
      "Select from a variety of chair and desk styles. Add objects like teacher's table, windows and plants. You can also customize how the student cards look like, and add portraits or avatars.",
    icon: ColorSwatchIcon,
  },
  {
    id: 'fullscreen',
    name: 'Save, print, share, or show your seating charts on fullscreen.',
    description:
      'Print your seating charts, share them with colleagues via a link, or display them on a big screen. Toggle between teacher and student views seamlessly.',
    icon: DeviceTabletIcon,
  },
];

export default function FeaturesClassroom() {
  return (
    <div className="bg-gray-900 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl sm:text-center">
          <p className="text-base font-semibold leading-7 text-indigo-400">
            Reduce your preparation time
          </p>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Efficient school seating chart generator
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-300">
            Seating Chart Maker simplifies the creation and management of school
            seating charts. Quickly generate new random arrangements and reuse
            room templates and student lists with ease.
          </p>
        </div>
      </div>
      <div className="relative overflow-hidden pt-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <StaticImage
            src="../../images/landing/classroom-features-04-24.png"
            alt="A screenshot of Seating Chart Maker software where a seating plan is being created for a lecture hall with 75 seats."
            className="mb-[-12%] rounded-xl shadow-2xl ring-1 ring-white/10"
            width={960}
          />
          <div className="relative" aria-hidden="true">
            <div className="absolute -inset-x-20 bottom-0 bg-gradient-to-t from-gray-900 pt-[7%]" />
          </div>
        </div>
      </div>
      <div className="mx-auto mt-16 max-w-7xl px-6 sm:mt-20 md:mt-24 lg:px-8">
        <dl className="mx-auto grid max-w-2xl grid-cols-1 gap-x-6 gap-y-10 text-base leading-7 text-gray-300 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-x-8 lg:gap-y-16">
          {features.map((feature) => (
            <div id={feature.id} key={feature.id} className="relative pl-9">
              <dt className="inline font-semibold text-white">
                <feature.icon
                  className="absolute left-1 top-1 h-5 w-5 text-indigo-500"
                  aria-hidden="true"
                />
                {feature.name}
              </dt>{' '}
              <dd className="inline">{feature.description}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
